import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  DISPLAY_CURRENCIES,
  PORTFOLIO_ACCOUNTS_PATH,
  TIME_RANGE_1_DAY,
} from "common/constants";
import { ConsolidatedAccountData, LanguageNameSpaces } from "@shared/types";
import {
  getFirstLastInGraphData,
  getPriceChangePercentage,
} from "features/dashboard/common/utils";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { getAccountExtraDetailsRoute } from "utils/routes";
import { sharedLib } from "@shared/services/shared.service";
import AccountsEmpty from "features/portfolio/Accounts/AccountsEmpty";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";
import { Surface } from "common/NewLook";
import { getNewLookTokens, newLookPhoneType } from "common/NewLook/tokens.ts";
import { DASHBOARD_ACCOUNT_ROW_ID } from "testIds";
import {
  PhoneCard,
  PhoneLoadingRow,
  PhoneMetaPair,
  PhonePill,
  PhoneRowDivider,
  PhoneSectionHead,
  PhoneSkeleton,
} from "./PhoneParts";
import { phoneMoney } from "./PhoneSummary";

interface PhoneAccountsListProps {
  /** Each account once (see useDashboardAccounts), so shares add up. */
  accountsData: ConsolidatedAccountData[];
  maxRows?: number;
}

interface AccountRow {
  key: string | number;
  clientId: number;
  name: string;
  exchange: string;
  total?: number;
  change?: number;
}

const RING_SIZE = 38;
const RING_COLORS = ["#3566D8", "#5E8AE8", "#8AAEF9", "#B5CBFB"];

/**
 * The phone dashboard's accounts: one card of rows, each with a ring showing
 * the account's share of the portfolio (its own slice at full strength).
 */
const PhoneAccountsList = ({
  accountsData,
  maxRows = 3,
}: PhoneAccountsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const { historyPush } = useHistoryWithQuery();
  const location = useLocation();
  const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
  const valueIndex = isUsd ? 1 : 2;
  const symbol = isUsd ? "$" : "₿";
  const { isLoading: portfolioLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const { data: clients, isLoading: clientsLoading } =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();
  const { onCreateAccountClick, whopModal } = useCreateAccountClick();

  const allRows: AccountRow[] = useMemo(() => {
    if (accountsData.length)
      return accountsData
        .slice()
        .sort((a, b) => b.total_in_usd - a.total_in_usd)
        .map((account) => {
          const graphData = account.graph_data?.[TIME_RANGE_1_DAY] || [];
          const [first, last] = getFirstLastInGraphData(graphData, valueIndex);
          return {
            key: account.client_uuid,
            clientId: account.client_id,
            name: account.client,
            exchange: account.exchange,
            total: isUsd ? account.total_in_usd : account.total_in_btc,
            change: graphData.length
              ? getPriceChangePercentage(first, last)
              : undefined,
          };
        });
    return (clients || [])
      .filter((client) => client.is_valid)
      .map((client) => ({
        key: client.client_id,
        clientId: client.client_id,
        name: client.client_name,
        exchange: client.exchange,
      }));
  }, [accountsData, clients, isUsd, valueIndex]);

  const rows = allRows.slice(0, maxRows);
  const portfolioTotal = allRows.reduce((sum, r) => sum + (r.total || 0), 0);
  const loading = !rows.length && (portfolioLoading || clientsLoading);
  const share = (row: AccountRow) =>
    portfolioTotal ? (row.total || 0) / portfolioTotal : 0;
  const restShare = Math.max(
    0,
    1 - allRows.slice(0, RING_COLORS.length).reduce((s, r) => s + share(r), 0)
  );

  const renderBody = () => {
    if (loading)
      return (
        <PhoneCard>
          {[0, 1, 2].map((index) => (
            <PhoneLoadingRow key={index}>
              <PhoneSkeleton $width={38} $height={38} $radius={19} />
              <Names>
                <PhoneSkeleton $width={120} $height={12} />
                <PhoneSkeleton $width={72} $height={10} />
              </Names>
              <PhoneSkeleton $width={64} $height={14} />
            </PhoneLoadingRow>
          ))}
        </PhoneCard>
      );
    if (!rows.length)
      return (
        <Surface $padding="12px 16px 20px" style={{ margin: "0 16px" }}>
          <AccountsEmpty buttonOnClick={onCreateAccountClick} iconWidth={96} />
        </Surface>
      );
    return (
      <PhoneCard>
        {rows.map((account, index) => {
          const percent = share(account) * 100;
          const percentText = `${
            percent > 0 && percent < 1 ? "<1" : Math.round(percent)
          }%`;
          const segments: [number, string][] = allRows
            .slice(0, RING_COLORS.length)
            .map((row, i) => [
              share(row),
              i === index
                ? RING_COLORS[i]
                : hexToRGB(RING_COLORS[i], theme.isDarkMode ? 0.4 : 0.33) ??
                  RING_COLORS[i],
            ]);
          if (restShare > 0.001)
            segments.push([
              restShare,
              theme.isDarkMode ? "rgba(255,255,255,0.12)" : "#E3E7EF",
            ]);
          const pending = account.total === undefined;
          return (
            <Row
              data-testid={DASHBOARD_ACCOUNT_ROW_ID}
              key={account.key}
              onClick={() =>
                historyPush(
                  getAccountExtraDetailsRoute(
                    location.pathname,
                    account.clientId
                  )
                )
              }
            >
              {pending ? (
                <PhoneSkeleton
                  $width={RING_SIZE}
                  $height={RING_SIZE}
                  $radius={RING_SIZE / 2}
                />
              ) : (
                <Ring segments={segments} />
              )}
              <Names>
                <AccountName title={account.name}>{account.name}</AccountName>
                <PhoneMetaPair
                  lead={account.exchange}
                  tail={
                    pending
                      ? undefined
                      : t("overview.share_of_portfolio", {
                          percent: percentText,
                        })
                  }
                  compactTail={percentText}
                />
              </Names>
              <Amounts>
                {account.total !== undefined ? (
                  <Amount>
                    {phoneMoney(account.total, symbol, precision)}
                  </Amount>
                ) : (
                  <PhoneSkeleton $width={64} $height={14} />
                )}
                {account.change !== undefined && (
                  <PhonePill $tone={account.change >= 0 ? "green" : "red"}>
                    {account.change > 0 ? "+" : account.change < 0 ? "−" : ""}
                    {Math.abs(account.change).toFixed(2)}%
                  </PhonePill>
                )}
              </Amounts>
            </Row>
          );
        })}
      </PhoneCard>
    );
  };

  return (
    <section>
      <PhoneSectionHead
        title={t("accounts.title")}
        seeAllTo={PORTFOLIO_ACCOUNTS_PATH}
        seeAllLabel={tCommon("labels.see_all")}
      />
      {renderBody()}
      {whopModal}
    </section>
  );
};

export default React.memo(PhoneAccountsList);

const Ring = ({ segments }: { segments: [number, string][] }) => {
  const radius = RING_SIZE / 2 - 4;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <RingSvg width={RING_SIZE} height={RING_SIZE} aria-hidden={true}>
      {segments.map(([part, color], index) => {
        const circle = (
          <circle
            key={index}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={5}
            strokeDasharray={`${Math.max(
              0,
              circumference * part - 2
            )} ${circumference}`}
            strokeDashoffset={-circumference * offset}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        );
        offset += part;
        return circle;
      })}
    </RingSvg>
  );
};

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  cursor: pointer;
  ${PhoneRowDivider};
`;
const RingSvg = styled.svg`
  flex: none;
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;
const Name = styled.div`
  ${newLookPhoneType.cardTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
`;
const Amount = styled.div`
  ${newLookPhoneType.rowFigure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
/** The account's name: a second line rather than an early ellipsis. */
const AccountName = styled(Name)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
  overflow-wrap: anywhere;
`;
const Amounts = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;
